import { useState, useEffect } from 'react'
import { getMetrics } from '../api/admin'
import type { AdminMetrics } from '../types'
import { Layout } from '../components/Layout'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { AdminUsersTable } from './AdminUsersTable'
import { AdminProjectsTable } from './AdminProjectsTable'
import styles from './AdminDashboard.module.css'

type Period = '7d' | '30d' | '90d'

const PERIOD_LABELS: Record<Period, string> = {
  '7d': 'Últimos 7 días',
  '30d': 'Últimos 30 días',
  '90d': 'Últimos 90 días',
}

interface MetricCardProps {
  label: string
  value: number | string
  highlight?: boolean
}

function MetricCard({ label, value, highlight = false }: MetricCardProps) {
  return (
    <div className={`${styles.metricCard} ${highlight ? styles.metricCardHighlight : ''}`}>
      <span className={styles.metricValue}>{value}</span>
      <span className={styles.metricLabel}>{label}</span>
    </div>
  )
}

export function AdminDashboard() {
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null)
  const [period, setPeriod] = useState<Period>('30d')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'users' | 'projects'>('users')

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await getMetrics(period)
        if (!cancelled) setMetrics(data)
      } catch {
        if (!cancelled) setError('No se pudieron cargar las métricas.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [period])

  return (
    <Layout>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>Panel de administración</h1>
          <div className={styles.periodSelector} role="group" aria-label="Período de métricas">
            {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
              <button
                key={p}
                className={`${styles.periodBtn} ${period === p ? styles.periodBtnActive : ''}`}
                onClick={() => setPeriod(p)}
                aria-pressed={period === p}
              >
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
        </div>

        <section aria-label="Métricas principales" className={styles.metricsSection}>
          {loading ? (
            <div className={styles.metricsLoading}>
              <LoadingSpinner size="md" message="Cargando métricas..." />
            </div>
          ) : error ? (
            <p className={styles.error}>{error}</p>
          ) : metrics ? (
            <div className={styles.metricsGrid}>
              <MetricCard label="Usuarios totales" value={metrics.users_total} highlight />
              <MetricCard label="Usuarios nuevos" value={metrics.users_new} />
              <MetricCard label="Usuarios activos" value={metrics.users_active} />
              <MetricCard label="Proyectos creados" value={metrics.projects_created} />
              <MetricCard label="Documentos generados" value={metrics.documents_generated} highlight />
            </div>
          ) : null}
        </section>

        <section className={styles.tablesSection} aria-label="Tablas de datos">
          <div className={styles.tabs} role="tablist" aria-label="Sección de datos">
            <button
              role="tab"
              aria-selected={activeTab === 'users'}
              className={`${styles.tab} ${activeTab === 'users' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('users')}
            >
              Usuarios
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'projects'}
              className={`${styles.tab} ${activeTab === 'projects' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('projects')}
            >
              Proyectos
            </button>
          </div>

          <div className={styles.tabContent} role="tabpanel">
            {activeTab === 'users' ? <AdminUsersTable /> : <AdminProjectsTable />}
          </div>
        </section>
      </div>
    </Layout>
  )
}
